import { useRef, useCallback } from 'react';
import { useNavbarDispatch } from '../../state/store';

/**
 * Hook for menu interaction handlers with hover timers.
 *
 * Encapsulates the complex hover/click behavior for dropdown menus:
 * - 100ms delay before opening on hover
 * - 200ms delay before closing on mouse leave
 * - Click-locking to prevent hover open after click
 *
 * @param menuKey - Unique identifier for the menu
 */
export const useMenuHandlers = ( menuKey: string ) => {
  const dispatch = useNavbarDispatch();
  const hoverTimersRef = useRef<Map<string, NodeJS.Timeout>>( new Map());

  const clearTimers = useCallback(() => {
    const openTimer = hoverTimersRef.current.get( `${menuKey}-open` );
    const closeTimer = hoverTimersRef.current.get( `${menuKey}-close` );
    if ( openTimer ) clearTimeout( openTimer );
    if ( closeTimer ) clearTimeout( closeTimer );
    hoverTimersRef.current.delete( `${menuKey}-open` );
    hoverTimersRef.current.delete( `${menuKey}-close` );
  }, [menuKey]);

  const handleMouseEnter = useCallback(( isClickLocked: boolean ) => {
    if ( isClickLocked ) return;

    // Clear any existing close timer
    const existingTimer = hoverTimersRef.current.get( `${menuKey}-close` );
    if ( existingTimer ) {
      clearTimeout( existingTimer );
      hoverTimersRef.current.delete( `${menuKey}-close` );
    }

    // Set open timer with delay
    const openTimer = setTimeout(() => {
      dispatch(
        { type: 'navbar/menu/open', payload: { menuKey }},
        { source: 'user' }
      );
      hoverTimersRef.current.delete( `${menuKey}-open` );
    }, 100 );
    hoverTimersRef.current.set( `${menuKey}-open`, openTimer );
  }, [ dispatch, menuKey ]);

  const handleMouseLeave = useCallback(() => {
    dispatch(
      { type: 'navbar/menu/unlock', payload: { menuKey }},
      { source: 'user' }
    );

    // Clear any existing open timer
    const existingTimer = hoverTimersRef.current.get( `${menuKey}-open` );
    if ( existingTimer ) {
      clearTimeout( existingTimer );
      hoverTimersRef.current.delete( `${menuKey}-open` );
    }

    // Set close timer with delay
    const closeTimer = setTimeout(() => {
      dispatch(
        { type: 'navbar/menu/close', payload: { menuKey }},
        { source: 'user' }
      );
      hoverTimersRef.current.delete( `${menuKey}-close` );
    }, 200 );
    hoverTimersRef.current.set( `${menuKey}-close`, closeTimer );
  }, [ dispatch, menuKey ]);

  const handleTitleClick = useCallback(( onNavigate: () => void ) => {
    dispatch(
      { type: 'navbar/menu/close', payload: { menuKey }},
      { source: 'user' }
    );
    dispatch(
      { type: 'navbar/menu/lock', payload: { menuKey }},
      { source: 'user' }
    );
    clearTimers();
    onNavigate();
  }, [ dispatch, menuKey, clearTimers ]);

  const handleItemClick = useCallback(( onNavigate: () => void ) => {
    dispatch(
      { type: 'navbar/menu/close', payload: { menuKey }},
      { source: 'user' }
    );
    clearTimers();
    onNavigate();
  }, [ dispatch, menuKey, clearTimers ]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleTitleClick,
    handleItemClick,
    clearTimers
  };
};

/**
 * Hook that returns a factory function for creating menu handlers.
 * Used when you need to create handlers for multiple menus in a loop.
 * Manages its own timer ref internally to avoid render-time ref access issues.
 */
export const useMenuHandlersFactory = () => {
  const dispatch = useNavbarDispatch();
  const hoverTimersRef = useRef<Map<string, NodeJS.Timeout>>( new Map());

  const createMenuHandlers = useCallback(( menuKey: string ) => {
    const clearTimers = () => {
      const openTimer = hoverTimersRef.current.get( `${menuKey}-open` );
      const closeTimer = hoverTimersRef.current.get( `${menuKey}-close` );
      if ( openTimer ) clearTimeout( openTimer );
      if ( closeTimer ) clearTimeout( closeTimer );
      hoverTimersRef.current.delete( `${menuKey}-open` );
      hoverTimersRef.current.delete( `${menuKey}-close` );
    };

    const handleMouseEnter = ( isClickLocked: boolean ) => {
      if ( isClickLocked ) return;

      const existingTimer = hoverTimersRef.current.get( `${menuKey}-close` );
      if ( existingTimer ) {
        clearTimeout( existingTimer );
        hoverTimersRef.current.delete( `${menuKey}-close` );
      }

      const openTimer = setTimeout(() => {
        dispatch(
          { type: 'navbar/menu/open', payload: { menuKey }},
          { source: 'user' }
        );
        hoverTimersRef.current.delete( `${menuKey}-open` );
      }, 100 );
      hoverTimersRef.current.set( `${menuKey}-open`, openTimer );
    };

    const handleMouseLeave = () => {
      dispatch(
        { type: 'navbar/menu/unlock', payload: { menuKey }},
        { source: 'user' }
      );

      const existingTimer = hoverTimersRef.current.get( `${menuKey}-open` );
      if ( existingTimer ) {
        clearTimeout( existingTimer );
        hoverTimersRef.current.delete( `${menuKey}-open` );
      }

      const closeTimer = setTimeout(() => {
        dispatch(
          { type: 'navbar/menu/close', payload: { menuKey }},
          { source: 'user' }
        );
        hoverTimersRef.current.delete( `${menuKey}-close` );
      }, 200 );
      hoverTimersRef.current.set( `${menuKey}-close`, closeTimer );
    };

    const handleTitleClick = ( onNavigate: () => void ) => {
      dispatch(
        { type: 'navbar/menu/close', payload: { menuKey }},
        { source: 'user' }
      );
      dispatch(
        { type: 'navbar/menu/lock', payload: { menuKey }},
        { source: 'user' }
      );
      clearTimers();
      onNavigate();
    };

    const handleItemClick = ( onNavigate: () => void ) => {
      dispatch(
        { type: 'navbar/menu/close', payload: { menuKey }},
        { source: 'user' }
      );
      clearTimers();
      onNavigate();
    };

    return {
      handleMouseEnter,
      handleMouseLeave,
      handleTitleClick,
      handleItemClick,
      clearTimers
    };
  }, [dispatch]);

  return createMenuHandlers;
};
